import { SearchOffOutlined } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { ProfileFieldGroup, ProfileTabValue, getTabForGroup } from "../profileFields";
import FieldGrid from "./FieldGrid";
import SectionCard from "./SectionCard";

type FieldSearchResultsProps = {
  groups: ProfileFieldGroup[];
  query: string;
  onOpenTab: (tab: ProfileTabValue) => void;
  onClear: () => void;
};

function FieldSearchResults({ groups, query, onOpenTab, onClear }: FieldSearchResultsProps) {
  const matchCount = groups.reduce((total, group) => total + group.fields.length, 0);

  if (!matchCount) {
    return (
      <Stack alignItems="center" gap={1} sx={{ py: 8, color: "text.tertiary" }}>
        <SearchOffOutlined sx={{ fontSize: "2.5rem" }} />
        <Typography level="title-md">No fields match “{query}”</Typography>
        <Typography level="body-sm" textColor="text.tertiary">
          Try a field name like “TICO”, “extension” or “bank”, or part of a value.
        </Typography>
        <Button size="sm" variant="soft" onClick={onClear} sx={{ mt: 1 }}>
          Clear search
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Typography level="body-sm" textColor="text.secondary">
        {matchCount} field{matchCount === 1 ? "" : "s"} matching “{query}”
      </Typography>
      <Box sx={{ columns: { xs: 1, lg: 2 }, columnGap: "20px" }}>
        {groups.map((group) => {
          const tab = getTabForGroup(group.id);

          return (
            <Box key={group.id} sx={{ breakInside: "avoid", mb: 2.5 }}>
              <SectionCard
                title={group.title}
                icon={group.icon}
                action={
                  tab && (
                    <Button size="sm" variant="plain" onClick={() => onOpenTab(tab.value)}>
                      Open in {tab.label}
                    </Button>
                  )
                }
              >
                <FieldGrid fields={group.fields} highlight={query} />
              </SectionCard>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

export default FieldSearchResults;
